// components/MyPage/MyItems.jsx
// components/MyPage/MyItems.jsx
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Stack,
  Pagination,
} from '@mui/material';
import SafeChip from '../Common/SafeChip';
import api from '../../api/axios';

// ✅ 안전한 이미지 경로 처리 함수
const getFullImageUrl = (url) =>
  url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const ITEMS_PER_PAGE = 6;

const MyItems = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  /* 보유 목록 */
  const fetchItems = () => {
    api.get('/api/rewards/items/my/')
      .then(res => setItems(res.data.items || []))
      .catch(() => alert('보유 아이템을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  };

  /* 사용 */
  const handleUseItem = (useritemId) => {
    if (!window.confirm('이 아이템을 사용하시겠습니까?')) return;

    api.post(`/api/rewards/items/use/${useritemId}/`)
      .then(res => {
        alert(res.data.message);
        fetchItems();
        onChanged?.();
      })
      .catch(() => alert('사용에 실패했습니다.'));
  };

  /* 환불 */
  const handleRefundItem = (useritemId) => {
    if (!window.confirm('이 아이템을 환불하시겠습니까?')) return;

    api.post('/api/rewards/items/refund/', { useritem_id: useritemId })
      .then(res => {
        alert(res.data.message);
        fetchItems();
        onChanged?.();
      })
      .catch(err => alert(err.response?.data?.message || '환불에 실패했습니다.'));
  };

  useEffect(() => { fetchItems(); }, []);

  /* 사용된 인스턴스 제외 후 item_id 묶기 */
  const groupedItems = useMemo(() => {
    const map = {};
    items
      .filter(it => !it.is_used)
      .forEach(it => {
        if (!map[it.item_id]) {
          map[it.item_id] = { ...it, total_quantity: it.quantity, instances: [it] };
        } else {
          map[it.item_id].total_quantity += it.quantity;
          map[it.item_id].instances.push(it);
        }
      });
    return Object.values(map);
  }, [items]);

  // 페이지네이션 로직
  const totalPages = Math.ceil(groupedItems.length / ITEMS_PER_PAGE);
  const paginatedItems = groupedItems.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // groupedItems 변경 시 페이지 리셋
  useEffect(() => {
    setPage(1);
  }, [groupedItems.length]);

  if (loading) return <Typography>로딩 중...</Typography>;
  if (groupedItems.length === 0) return <Typography>현재 보유 중인 아이템이 없습니다.</Typography>;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        🎫 보유한 아이템
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {paginatedItems.map(item => (
          <Box
            key={item.item_id}
            sx={{
              width: { xs: '100%', sm: '48%', md: '32%' },
              flexGrow: 1,
              height: { xs: 'auto', sm: 240 },
            }}
          >
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                boxSizing: 'border-box',
              }}
            >
              {item.image && (
                <Avatar
                  variant="rounded"
                  src={getFullImageUrl(item.image)} // ✅ 안전하게 처리
                  alt={item.name}
                  sx={{ width: 72, height: 72, mb: 1 }}
                />
              )}

              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.name}
                  {item.total_quantity > 1 && (
                    <Typography component="span" color="text.secondary" fontSize="0.9rem">
                      {' '}×{item.total_quantity}
                    </Typography>
                  )}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  획득일: {item.acquired_at}
                </Typography>
              </Box>

              {item.instances[0].quantity > 0 ? (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => handleUseItem(item.instances[0].useritem_id)}
                  >
                    사용하기
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleRefundItem(item.instances[0].useritem_id)}
                  >
                    환불하기
                  </Button>
                </Stack>
              ) : (
                <SafeChip label="수량 없음" color="default" size="small" sx={{ mt: 1 }} />
              )}
            </Paper>
          </Box>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default MyItems;
